import * as React from "react";
import {
  Button,
  Form,
  Nav,
  Navbar,
  Tab,
  Spinner,
  ListGroup,
} from "react-bootstrap";
import { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { bindActionCreators } from "redux";
import { useSelector, useDispatch } from "react-redux";
import { actionCreators } from "../../redux/index";
import { HttpContext } from "../../api-services/HttpService";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import CCOffcanvas from "../sub-components/CCOffcanvas.tsx";
import AddFavoritesCanvas from "../sub-components/AddFavoritesCanvas.tsx";
import PageContext from "./../sub-components/PageContext";
import SnackbarAlert from "../sub-components/SnackbarAlert.jsx";
import "../../sass/_favRecRecords.scss";
import FavoriteLinks from "../../assets/favoritesLinks.json";
import RecentRecordLinks from "../../assets/recentRecordLinks.json";
import infoIcon from "../../svgs/info.svg";
import PageTooltip from "../sub-components/PageTooltip.jsx";

type Props = {
  title?: string;
  show?: boolean;
  onCanvasClose: () => void;
};

export type Favorites = {
  link?: string;
  name?: string;
  id?: string;
};

//Populate only link details from full favorite details
const populateAllFavLinks = () => {
  let favLinks = [];
  FavoriteLinks.data.forEach((item) => {
    item.links.map((favorite) => {
      favLinks.push(favorite);
    });
  });
  return favLinks;
};

const FavoritesRecentRecordsTabs = ({ title, show, onCanvasClose }: Props) => {
  const { GET } = useContext(HttpContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    setCompany,
    setCompanyInfo,
    setBackPath,
    setTransactionDetails,
    setConsortiumId,
  } = bindActionCreators(actionCreators, dispatch);
  const [currentTab, setCurrentTab] = useState("favorites");
  const [addFavoritesCanvas, setAddFavoritesCanvas] = useState(false);
  const [currentFavorites, setCurrentFavorites] = useState([]);
  const [allFavLinks, setAllFavLinks] = useState(populateAllFavLinks);
  const [addFavButtonDisabled, setAddFavButtonDisabled] = useState(true);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [recentRecordsLoading, setRecentRecordsLoading] = useState(false);
  const [favoriteMessage, setFavoriteMessage] = useState("");
  const [reloadFavorites, setReloadFavorites] = useState(false);
  const [recentRecords, setRecentRecords] = useState([]);
  const [recentRecordsMessage, setRecentRecordsMessage] = useState("");
  // initialize state for snackbar
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarColor, setSnackbarColor] = useState("info");

  // function to set snackbar state
  const snackbarAlert = (show, message, color) => {
    setSnackbarMessage((snackbarMessage) => (snackbarMessage = message));
    setSnackbarColor((snackbarColor) => (snackbarColor = color));
    setShowSnackbar((showSnackbar) => (showSnackbar = true));
  };

  // object to pass variables to context
  const settings = {
    showSnackbar,
    setShowSnackbar,
    snackbarColor,
    snackbarMessage,
    snackbarAlert,
  };

  //handle tab change
  const handleSelect = (eventKey) => {
    setCurrentTab(eventKey);
    if (eventKey === "favorites") {
      setRecentRecords([]);
      setRecentRecordsMessage("");
    } else {
      loadRecentLinks(eventKey);
    }
  };

  //call the recent records api
  const getRecentRecords = () => {
    setRecentRecordsLoading(true);
    return new Promise((resolve, reject) => {
      GET("/links/recent")
        .then((res) => {
          let tempRecords = [];
          res.data.recentRecords.map((item) => {
            let recentRecord = item;
            recentRecord.name = RecentRecordLinks[item.path].name;
            tempRecords.push(recentRecord);
          });
          if (tempRecords.length == 0) {
            setRecentRecordsMessage("No Recent Records");
          }
          setRecentRecords(tempRecords);
          resolve(res);
        })
        .catch((err) => {
          setRecentRecordsMessage(
            "Technical error while retrieving Recent Records, Please contact your administrator"
          );
          reject(err);
        })
        .finally(() => {
          setRecentRecordsLoading(false);
        });
    });
  };

  //fetch the link for the recent records to be redirected from the offcanvas
  const fetchLinkDetails = (item) => {
    if (RecentRecordLinks[item.path].hasBackPath) {
      setBackPath(item.backpath);
    }
    if (
      RecentRecordLinks[item.path].hasTransactionDetails &&
      item.transactiondetails
    ) {
      setTransactionDetails(
        item.transactiondetails ? JSON.parse(item.transactiondetails) : ""
      );
    }
    if (RecentRecordLinks[item.path].hasCompany && item.company) {
      setCompany(item.company);
    }
    if (RecentRecordLinks[item.path].hasCompanyInfo && item.companyinfo) {
      setCompanyInfo(item.companyinfo ? JSON.parse(item.companyinfo) : "");
    }
    if (RecentRecordLinks[item.path].hasConsortiumId && item.consortiumid) {
      setConsortiumId(item.consortiumid);
    }
    navigate(item.path);
  };

  //main recent links api call
  const loadRecentLinks = (eventKey) => {
    getRecentRecords()
      .then((res) => {
        console.log(res);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  //fetch title to be displayed in the recent records in the offcanvas
  const fetchTitle = (item) => {
    if (
      RecentRecordLinks[item.path].hasCompany === false &&
      RecentRecordLinks[item.path].hasConsortiumId === false
    ) {
      return <span>{item.name}</span>;
    } else if (
      item?.consortiumid &&
      RecentRecordLinks[item.path].hasConsortiumId
    ) {
      return item?.consortiumid?.length < 40 ? (
        <span>
          {item.consortiumid}
          <br /> {item.name}
        </span>
      ) : (
        <span>
          {item.consortiumid.slice(0, 40)}
          <PageTooltip title={`${item.consortiumid}`}>
            <img src={`${infoIcon} `} className="data-table-icon" />
          </PageTooltip>
          <br />
          {item.name}
        </span>
      );
    } else if (
      RecentRecordLinks[item.path].hasCompany === true &&
      !item.company
    ) {
      return <span>{item.name}</span>;
    } else if (item?.company && RecentRecordLinks[item.path].hasCompany) {
      return item?.company.length < 40 ? (
        <span>
          {item.company}
          <br /> {item.name}
        </span>
      ) : (
        <span>
          {item.company.slice(0, 40)}
          <PageTooltip title={`${item.company}`}>
            <img src={`${infoIcon} `} className="data-table-icon" />
          </PageTooltip>
          <br />
          {item.name}
        </span>
      );
    } else{
        return <span>{item.name}</span>;
        }
  };

  // get favorites from favorite endpoint
  const getCurrentFavLinks = () => {
    setAddFavButtonDisabled(true);
    setFavoritesLoading(true);
    return new Promise((resolve, reject) => {
      GET("/links/favorite")
        .then((res) => {
          let tempFavs = [];
          res.data.favoriteRecords.map((item) => {
            let favorites = allFavLinks.find(
              (value) => value.link === item.path
            );
            tempFavs.push(favorites);
          });
          if (tempFavs?.length === 0) {
            setFavoriteMessage("No Favorites");
            setAddFavButtonDisabled(false);
          } else {
            setFavoriteMessage("");
          }
          setCurrentFavorites(tempFavs);
          setFavoritesLoading(false);
          resolve(res);
        })
        .catch((err) => {
          setFavoritesLoading(false);
          setFavoriteMessage(
            "Technical error while retrieving Favorites, Please contact your administrator"
          );
          reject(err);
          setAddFavButtonDisabled(true);
        });
    });
  };

  // use effect to enable add favorites button after the saved favorites load
  useEffect(() => setAddFavButtonDisabled(false), [currentFavorites]);

  // useEffect for initial render
  useEffect(() => {
    // make initial get request to get display favorites from database
    getCurrentFavLinks()
      .then((res) => {
        console.log(res);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [allFavLinks, reloadFavorites]);

  //on closing the offcanvas the below actions need to be performed
  const resetTabs = () => {
    onCanvasClose();
    setCurrentTab("favorites");
    setRecentRecords([]);
    setRecentRecordsMessage("");
  };

  return (
    <PageContext.Provider value={settings}>
      <SnackbarAlert />
      <CCOffcanvas title={title} show={show} onCanvasClose={resetTabs}>
        <Tab.Container
          defaultActiveKey="favorites"
          activeKey={currentTab}
          onSelect={handleSelect}
        >
          <Nav
            className="st-card tab-bg"
            fill
            variant="tabs"
            defaultActiveKey="favorites"
          >
            <Nav.Item className="mt-2 nav-item-offcanvas">
              <Nav.Link
                eventKey="favorites"
                key="favorites"
                className="nav-link nav-link-offcanvas"
              >
                Favorites
              </Nav.Link>
            </Nav.Item>
            <Nav.Item className="mt-2 nav-item-offcanvas">
              <Nav.Link
                key="recentLinks"
                eventKey="recentLinks"
                className="nav-link nav-link-offcanvas"
              >
                Recent Links
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <Tab.Content style={{ height: "80vh" }}>
            <Tab.Pane eventKey="favorites" style={{ height: "80vh" }}>
              {favoritesLoading ? (
                <div className="loader-class mt-4">
                  Loading content...Please wait...
                  <Spinner animation="border" />
                </div>
              ) : null}
              {favoriteMessage ? (
                <p className="loader-class  mt-4">{favoriteMessage}</p>
              ) : (
                <Form.Group
                  className="mb-3  mt-3"
                  controlId="formBasicName"
                  style={{ position: "relative", height: "80vh" }}
                >
                  {currentFavorites?.map((item) => {
                    return (
                      item && (
                        <div
                          className="dataset-radio-options fav-links-item"
                          key={item.id}
                        >
                          <Nav.Link
                            key={item.id}
                            eventKey="favoriteLinks"
                            className="p-1 nav-link nav-link-offcanvas"
                            href={item.link}
                          >
                            {" "}
                            <div className="fav-links-icon">
                              <span className="material-symbols-outlined me-2">
                                leaderboard
                              </span>
                              <div className="fav-link-underline">
                                {item.name}
                              </div>
                              <hr />
                            </div>
                          </Nav.Link>
                        </div>
                      )
                    );
                  })}
                </Form.Group>
              )}
              <Button
                className="modal-button add-favorites-button"
                onClick={() => setAddFavoritesCanvas(true)}
                disabled={addFavButtonDisabled}
              >
                Add Favorites
              </Button>

              <AddFavoritesCanvas
                title="Add Favorites"
                show={addFavoritesCanvas}
                onCanvasClose={() => setAddFavoritesCanvas(false)}
                savedFavorites={currentFavorites}
                setSavedFavorites={setCurrentFavorites}
                reloadFavorites={reloadFavorites}
                setReloadFavorites={setReloadFavorites}
                allFavLinks={allFavLinks}
              />
            </Tab.Pane>
            <Tab.Pane eventKey="recentLinks">
              {recentRecordsLoading ? (
                <div className="loader-class mt-4">
                  Loading content...Please wait...
                  <Spinner animation="border" />
                </div>
              ) : null}
              {recentRecordsMessage ? (
                <p className="loader-class  mt-4">{recentRecordsMessage}</p>
              ) : (
                <Form.Group
                  className="mb-3  mt-3 "
                  controlId="recentRecords"
                  style={{ position: "relative", height: "80vh" }}
                >
                  {recentRecords?.map((item) => {
                    return (
                      item &&
                      item?.path && (
                        <div
                          className="dataset-radio-options recent-links-item"
                          key={item.id}
                        >
                          <Nav.Link
                            key={item.path}
                            eventKey="recentLinks"
                            className="p-1 nav-link nav-recent-link-offcanvas"
                            onClick={() => fetchLinkDetails(item)}
                          >
                            {" "}
                            <div className="fav-links-icon">
                              <span className="material-symbols-outlined me-2">
                                description
                              </span>
                              <div className="recent-links-text">
                                {fetchTitle(item)}
                              </div>
                            </div>
                          </Nav.Link>
                        </div>
                      )
                    );
                  })}
                </Form.Group>
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </CCOffcanvas>
    </PageContext.Provider>
  );
};

export default FavoritesRecentRecordsTabs;
